import { css, html } from "lit";
import { artImage, type ChromeArt } from "../../chrome/chromeArt";
import { fallbackLabels, type LabelTable, type Labels } from "../../chrome/chromeLabels";
import { ChromeElement, defineChromeElement, pressFeedback } from "../../chrome/ChromeElement";
import type { InfoHtmlDocument } from "./html/InfoHtmlNode";
import { renderInfoHtmlToDom } from "./html/renderInfoHtmlToDom";
import type { ShellInfoPageBindings, ShellInfoPagePort } from "./ShellInfoPagePort";
import { fitScale } from "@slot/presentation";

/** The `InfoPanel` prefab geometry (design units, y down). */
const PAGE = {
  backdropAlpha: 232 / 255,
  /** `ContentRoot`: 20 in from the top and bottom edges. */
  contentInsetY: 20,
  /** `Header` 1080×119.4, top-pinned; scale-mode (desktop max 1080×100, mobile max 1920 wide, landscape reference height 100). */
  header: { width: 1080, height: 119.4, desktopMaxWidth: 1080, desktopMaxHeight: 100, mobileMaxWidth: 1920, landscapeReferenceHeight: 100 },
  title: { size: 50 },
  /** `CloseBtn` 110², right 100.3, 2.315 above the header's centre line. */
  close: { size: 110, right: 100.3, yUp: 2.315 },
  /** `ScrollView` 1500×1733, scale-mode (max 1500 wide; mobile landscape unlimited, reference height 1000), top 147.4 inside the content root. */
  scroll: { width: 1500, height: 1733, maxWidth: 1500, top: 147.4, landscapeReferenceHeight: 1000 },
  /** `view` inset 30 inside the scroll view. */
  viewInset: 30,
} as const;

export const INFO_PAGE_LABELS = { title: ["info.title", "Info"], close: ["common.close", "Close"] } as const satisfies LabelTable;
export type ShellInfoPageLabels = Labels<typeof INFO_PAGE_LABELS>;

export const INFO_PAGE_TAG = "slot-info-page";

/**
 * `<slot-info-page>`: a full-screen backdrop, a top-pinned header with the title and the close
 * button, and a native scroll area holding the rendered template (`renderInfoHtmlToDom`). The
 * factory attaches the image resolver and the close icon; the presenter drives the port.
 */
export class ShellInfoPageElement extends ChromeElement implements ShellInfoPagePort {
  public static override properties = {
    ...ChromeElement.properties,
    open: { type: Boolean, reflect: true },
    labels: { attribute: false },
    heading: { state: true },
    art: { attribute: false },
  };

  public declare open: boolean;
  public declare labels: ShellInfoPageLabels;
  /** The loaded document's title; the label until one loads. */
  private declare heading: string | null;
  public declare art: ChromeArt;
  /** `<img src>` → data URL (null leaves the slot empty). Attached by the factory. */
  public resolveImage: (src: string) => Promise<string | null> = async () => null;

  private bindings: ShellInfoPageBindings | null = null;
  private pendingDocument: InfoHtmlDocument | null = null;

  public constructor() {
    super();
    this.open = false;
    this.labels = fallbackLabels(INFO_PAGE_LABELS);
    this.heading = null;
    this.art = {};
  }

  public static override styles = css`
    :host { display: none; position: absolute; inset: 0; pointer-events: auto; font-weight: 500; color: #fff; --du: 1px; }
    :host([open]) { display: block; }
    .backdrop { position: absolute; inset: 0; background: rgba(0, 0, 0, ${PAGE.backdropAlpha}); }
    .content { position: absolute; inset: 0; pointer-events: none; }
    .header { position: absolute; left: 50%; top: ${PAGE.contentInsetY}px; width: ${PAGE.header.width}px; height: ${PAGE.header.height}px; transform: translateX(-50%) scale(var(--header-scale, 1)); transform-origin: top center; pointer-events: none; }
    .title { position: absolute; left: 0; right: 0; top: 50%; transform: translateY(-50%); text-align: center; font-size: ${PAGE.title.size}px; line-height: 1; white-space: nowrap; }
    .close { position: absolute; right: ${PAGE.close.right}px; top: calc(50% - ${PAGE.close.yUp}px); width: ${PAGE.close.size}px; height: ${PAGE.close.size}px; transform: translateY(-50%); margin: 0; padding: 0; border: 0; background: transparent no-repeat center / 100% 100%; cursor: pointer; pointer-events: auto; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
    .close:hover { filter: brightness(0.39); }
    .close:active { filter: brightness(0.73); }
    .scroll { position: absolute; left: 50%; transform: translateX(-50%); overflow-x: hidden; overflow-y: auto; overscroll-behavior: contain; touch-action: pan-y; pointer-events: auto; -webkit-overflow-scrolling: touch; }
    .body { padding: calc(var(--du) * ${PAGE.viewInset}); }
    .slot-info-heading { line-height: 1.15; }
    .slot-info-paragraph { margin: 0; white-space: pre-line; }
    .slot-info-image { display: block; background: transparent no-repeat center / 100% 100%; flex-shrink: 0; }
    .slot-info-table-row { display: flex; flex-direction: row; align-items: center; }
    .slot-info-winline { display: grid; gap: calc(var(--du) * 2); }
    .slot-info-winline-cell { display: block; width: calc(var(--du) * 24); height: calc(var(--du) * 24); background: #e0e0e0; }
    .slot-info-winline-cell.is-active { background: #e4b94a; }
  `;

  public override connectedCallback(): void {
    super.connectedCallback();
    this.dataset.slot = "info-page";
  }

  public bind(bindings: ShellInfoPageBindings): void {
    this.bindings = bindings;
  }

  public setOpen(open: boolean): void {
    this.open = open;
  }

  public resetScrollToTop(): void {
    const scroll = this.renderRoot.querySelector<HTMLElement>(".scroll");
    if (scroll) scroll.scrollTop = 0;
  }

  public setHtmlContent(doc: InfoHtmlDocument): void {
    this.heading = doc.title;
    this.pendingDocument = doc;
    this.applyPendingDocument();
  }

  protected override updated(): void {
    this.applyPendingDocument();
  }

  private applyPendingDocument(): void {
    const body = this.renderRoot.querySelector<HTMLElement>(".body");
    if (!body || !this.pendingDocument) return;
    const doc = this.pendingDocument;
    this.pendingDocument = null;
    renderInfoHtmlToDom(doc, body, { resolveImage: (src) => this.resolveImage(src) });
  }

  /** The two authored window layouts (header, scroll view) over the design viewport. */
  private layout() {
    const v = this.view.design;
    const contentHeight = v.height - 2 * PAGE.contentInsetY;
    const h = PAGE.header;
    const headerScale = fitScale(
      { width: h.width, height: this.isLandscape ? h.landscapeReferenceHeight : h.height },
      this.isDesktop ? { maxWidth: h.desktopMaxWidth, maxHeight: h.desktopMaxHeight } : { maxWidth: h.mobileMaxWidth },
      { width: v.width, height: contentHeight },
    );
    const s = PAGE.scroll;
    const scrollScale = fitScale({ width: s.width, height: this.isLandscape ? s.landscapeReferenceHeight : s.height }, this.isLandscape ? {} : { maxWidth: s.maxWidth }, { width: v.width, height: contentHeight });
    return { headerScale, scrollScale };
  }

  protected override render() {
    const { headerScale, scrollScale } = this.layout();
    return html`
      <div class="backdrop" data-slot="info-backdrop" @click=${() => this.bindings?.onBackdrop()}></div>
      <div class="content">
        <div class="header" style="--header-scale: ${headerScale}">
          <div class="title" data-slot="info-title">${this.heading ?? this.labels.title}</div>
          <button class="close" data-slot="info-close" type="button" aria-label=${this.labels.close} style=${artImage(this.art, "closeRed")} @pointerdown=${pressFeedback} @click=${() => this.bindings?.onClose()}></button>
        </div>
        <div class="scroll" data-slot="info-scroll" style="top: ${PAGE.contentInsetY + PAGE.scroll.top}px; bottom: ${PAGE.contentInsetY}px; width: ${PAGE.scroll.width * scrollScale}px">
          <div class="body" data-slot="info-body" style="--du: ${scrollScale}px"></div>
        </div>
      </div>
    `;
  }
}

export function defineShellInfoPageElement(): void {
  defineChromeElement(INFO_PAGE_TAG, ShellInfoPageElement);
}
